import { z } from 'zod';

/** Cable routes (PRD §5.5). */
export const ROUTE_MODES = ['road', 'straight', 'manual'] as const;
export type RouteMode = (typeof ROUTE_MODES)[number];
export const ROUTE_MODE_LABELS: Record<RouteMode, string> = {
  road: 'Ikuti jalan',
  straight: 'Garis lurus',
  manual: 'Manual',
};

export const ROUTE_STATUSES = ['planned', 'installed', 'maintenance'] as const;
export type RouteStatus = (typeof ROUTE_STATUSES)[number];
export const ROUTE_STATUS_LABELS: Record<RouteStatus, string> = {
  planned: 'Rencana',
  installed: 'Terpasang',
  maintenance: 'Maintenance',
};

export const ROUTE_CATEGORIES = ['backbone', 'feeder', 'distribution', 'drop'] as const;
export type RouteCategory = (typeof ROUTE_CATEGORIES)[number];
export const ROUTE_CATEGORY_LABELS: Record<RouteCategory, string> = {
  backbone: 'Backbone',
  feeder: 'Feeder',
  distribution: 'Distribusi',
  drop: 'Drop',
};

export const INSTALL_METHODS = ['aerial', 'underground'] as const;
export type InstallMethod = (typeof INSTALL_METHODS)[number];
export const INSTALL_METHOD_LABELS: Record<InstallMethod, string> = {
  aerial: 'Udara (tiang)',
  underground: 'Tanam',
};

export const ROUTE_COLOR_PATTERN = /^#[0-9a-f]{6}$/;

/** Route fields a technician may change (PRD §3.1); shape, cable and capacity are technical. */
export const ROUTE_FIELD_LEVEL_KEYS = ['status', 'notes'] as const;

export type LngLat = [number, number];

const position = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);
export const routePathSchema = z
  .array(position)
  .min(2, 'Jalur butuh minimal 2 titik')
  .max(5000, 'Jalur terlalu banyak titik');

const trimBlank = (v: unknown) => (typeof v === 'string' ? (v.trim() === '' ? null : v.trim()) : v);
const toNum = (v: unknown) => {
  const t = trimBlank(v);
  return typeof t === 'string' ? Number(t.replace(',', '.')) : t;
};

/** Route attributes (create and full update). The path goes with create or via the path endpoint. */
export const routeAttributesSchema = z.object({
  name: z.preprocess(trimBlank, z.string().max(120, 'Nama maksimal 120 karakter').nullable().default(null)),
  category: z.enum(ROUTE_CATEGORIES).default('distribution'),
  cableType: z.preprocess(trimBlank, z.string().max(20).nullable().default(null)),
  coreCount: z.preprocess(
    (v) => toNum(v) ?? null,
    z
      .number({ invalid_type_error: 'Jumlah core harus angka' })
      .int('Jumlah core bilangan bulat')
      .min(1, 'Minimal 1 core')
      .max(288, 'Maksimal 288 core')
      .nullable(),
  ),
  status: z.enum(ROUTE_STATUSES).default('planned'),
  installMethod: z.enum(INSTALL_METHODS).default('aerial'),
  slackPct: z.preprocess(
    (v) => toNum(v) ?? 0,
    z.number({ invalid_type_error: 'Slack harus angka' }).min(0, 'Slack 0–100%').max(100, 'Slack 0–100%'),
  ),
  notes: z.preprocess(trimBlank, z.string().max(2000).nullable().default(null)),
  /** Line colour on the map; null = default colour for the status. */
  color: z.preprocess((v) => {
    const t = trimBlank(v);
    return typeof t === 'string' ? t.toLowerCase() : (t ?? null);
  }, z.string().regex(ROUTE_COLOR_PATTERN, 'Warna harus format #rrggbb').nullable().default(null)),
});
export type RouteAttributes = z.infer<typeof routeAttributesSchema>;

export const routeCreateSchema = routeAttributesSchema.extend({
  fromAssetId: z.string().uuid(),
  toAssetId: z.string().uuid(),
  mode: z.enum(ROUTE_MODES),
  path: routePathSchema,
});
export type RouteCreateInput = z.infer<typeof routeCreateSchema>;

export const routePathUpdateSchema = z.object({ mode: z.enum(ROUTE_MODES), path: routePathSchema });

export const routePreviewSchema = z.object({
  fromAssetId: z.string().uuid(),
  toAssetId: z.string().uuid(),
  mode: z.enum(['road', 'straight']),
});

export const polesOptionsSchema = z.object({
  intervalM: z.number().int().min(10, 'Interval 10–100 m').max(100, 'Interval 10–100 m').default(30),
  atTurns: z.boolean().default(true),
});
export type PolesOptions = z.infer<typeof polesOptionsSchema>;

export const routeListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  q: z.string().trim().max(100).optional(),
  status: z.enum(ROUTE_STATUSES).optional(),
  category: z.enum(ROUTE_CATEGORIES).optional(),
  area: z.string().max(40).optional(),
  trash: z
    .enum(['0', '1', 'true', 'false'])
    .optional()
    .transform((v) => v === '1' || v === 'true'),
});
export type RouteListQuery = z.infer<typeof routeListQuerySchema>;

export interface RouteEnd {
  id: string;
  code: string;
  name: string;
  typeCode: string;
  areaCode: string | null;
}

export interface RouteListItem {
  id: string;
  name: string | null;
  from: RouteEnd;
  to: RouteEnd;
  mode: RouteMode;
  status: RouteStatus;
  category: RouteCategory;
  cableType: string | null;
  coreCount: number | null;
  color: string | null;
  lengthM: number;
  cableLengthM: number;
  poles: number;
  areaCode: string | null;
  deletedAt: string | null;
}

export interface RoutePage {
  items: RouteListItem[];
  page: number;
  pageSize: number;
  total: number;
  /** Sum of path lengths for the whole filter, not just this page. */
  totalLengthM: number;
}

export interface RouteDetail extends RouteListItem {
  installMethod: InstallMethod;
  slackPct: number;
  notes: string | null;
  cableTypeName: string | null;
  path: LngLat[];
  cost: { cable: number | null; poles: number | null; total: number | null };
  autoPoleIntervalM: number | null;
  autoPoleAtTurns: boolean | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

export interface PolePreview {
  poles: Array<{ lng: number; lat: number; reason: 'interval' | 'turn' }>;
  /** Candidates dropped because a pole (or the route end asset) already stands within 10 m. */
  skippedNearExisting: number;
  replacesAuto: number;
  unitPrice: number | null;
  totalCost: number | null;
}

// ---------------------------------------------------------------- geometry helpers

const R = 6_371_008.8;
const rad = (d: number) => (d * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;

/** Great-circle distance in metres. */
export function distanceM(a: LngLat, b: LngLat): number {
  const dLat = rad(b[1] - a[1]);
  const dLng = rad(b[0] - a[0]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[1])) * Math.cos(rad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Drawn route ends closer than this to their asset are moved onto the asset. */
export const ROUTE_END_SNAP_M = 5;

/**
 * A route path that starts and ends exactly on its assets. An end drawn within `snapM` of its asset
 * is moved onto it; a farther end is kept and the asset added after it, so a line imported from
 * Google Earth keeps its exact shape plus the short connection to the asset.
 */
export function attachEnds(
  path: readonly LngLat[],
  from: LngLat,
  to: LngLat,
  snapM = ROUTE_END_SNAP_M,
): LngLat[] {
  const inner = path.slice(1, -1);
  const first = path[0];
  const last = path.length > 1 ? path[path.length - 1] : undefined;
  const head: LngLat[] = first && distanceM(first, from) > snapM ? [from, first] : [from];
  const tail: LngLat[] = last && distanceM(last, to) > snapM ? [last, to] : [to];
  return [...head, ...inner, ...tail];
}

export function pathLengthM(path: readonly LngLat[]): number {
  let total = 0;
  for (let i = 1; i < path.length; i++) total += distanceM(path[i - 1]!, path[i]!);
  return total;
}

function bearing(a: LngLat, b: LngLat): number {
  const y = Math.sin(rad(b[0] - a[0])) * Math.cos(rad(b[1]));
  const x =
    Math.cos(rad(a[1])) * Math.sin(rad(b[1])) -
    Math.sin(rad(a[1])) * Math.cos(rad(b[1])) * Math.cos(rad(b[0] - a[0]));
  return (deg(Math.atan2(y, x)) + 360) % 360;
}

function interpolate(a: LngLat, b: LngLat, t: number): LngLat {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

/**
 * Pole positions along a route (RTE-09): one every `intervalM` metres and, when `atTurns`, one at
 * each vertex where the direction changes by more than `turnDeg`. Nothing is placed within half of
 * `minGapM` of the route ends (the assets), and candidates closer than `minGapM` to the previous one are merged.
 */
export function planPoles(
  path: readonly LngLat[],
  opts: { intervalM: number; atTurns: boolean; turnDeg?: number; minGapM?: number },
): Array<{ lng: number; lat: number; reason: 'interval' | 'turn' }> {
  const turnDeg = opts.turnDeg ?? 30;
  const minGap = opts.minGapM ?? 10;
  const out: Array<{ pos: LngLat; reason: 'interval' | 'turn' }> = [];
  const start = path[0]!;
  const end = path[path.length - 1]!;
  // Only skip spots practically on top of the end assets.
  const farFromEnds = (p: LngLat) => distanceM(p, start) >= minGap / 2 && distanceM(p, end) >= minGap / 2;
  const place = (pos: LngLat, reason: 'interval' | 'turn') => {
    if (!farFromEnds(pos)) return;
    const last = out[out.length - 1];
    if (last && distanceM(last.pos, pos) < minGap) {
      if (reason === 'turn') last.reason = 'turn';
      return;
    }
    out.push({ pos, reason });
  };

  let sinceLast = 0;
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1]!;
    const b = path[i]!;
    const seg = distanceM(a, b);
    let travelled = 0;
    while (sinceLast + (seg - travelled) >= opts.intervalM) {
      travelled += opts.intervalM - sinceLast;
      place(interpolate(a, b, travelled / seg), 'interval');
      sinceLast = 0;
    }
    sinceLast += seg - travelled;

    if (opts.atTurns && i < path.length - 1) {
      let turn = Math.abs(bearing(a, b) - bearing(b, path[i + 1]!));
      if (turn > 180) turn = 360 - turn;
      if (turn > turnDeg) {
        place(b, 'turn');
        sinceLast = 0;
      }
    }
  }
  return out.map((p) => ({ lng: round7(p.pos[0]), lat: round7(p.pos[1]), reason: p.reason }));
}

const round7 = (n: number) => Math.round(n * 1e7) / 1e7;
